import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Shield, FileCheck, Download, Trash2, UserCheck, UserX, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import PermissionMatrixDialog from './PermissionMatrixDialog';
import UserEditDialog from './UserEditDialog';
import UserBadge from '../common/UserBadge';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const downloadPdf = (dataUrl, filename) => {
  const base64 = dataUrl.split(',')[1];
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

export default function UserManagement() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [permDialog, setPermDialog] = useState({ open: false, user: null });
  const [editDialog, setEditDialog] = useState({ open: false, user: null });
  const [certLoading, setCertLoading] = useState(null);
  const [branches, setBranches] = useState([]);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    name: '',
    email: '',
    role: 'Data Entry Operator',
    department: '',
    designation: '',
    branch_id: '',
    photo_url: '',
  });

  useEffect(() => {
    fetchUsers();
    fetchRoles();
    axios.get(`${API_URL}/api/branches`, { withCredentials: true })
      .then(({ data }) => setBranches(data))
      .catch(() => setBranches([]));
  }, []);

  const fetchUsers = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/users`, {
        withCredentials: true,
      });
      setUsers(data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/users/roles`, {
        withCredentials: true,
      });
      setRoles(data);
    } catch (error) {
      console.error('Failed to fetch roles:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/users`, formData, {
        withCredentials: true,
      });
      toast.success('User created successfully');
      setDialogOpen(false);
      setFormData({
        username: '',
        password: '',
        name: '',
        email: '',
        role: 'Data Entry Operator',
        department: '',
        designation: '',
        branch_id: '',
        photo_url: '',
      });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleToggleStatus = async (user) => {
    const nextActive = !user.is_active;
    try {
      await axios.put(`${API_URL}/api/users/${user.id}/status`, { is_active: nextActive }, { withCredentials: true });
      toast.success(`User ${nextActive ? 'activated' : 'deactivated'}`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Permanently delete user "${user.username}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/users/delete/${user.id}`, { withCredentials: true });
      toast.success('User deleted');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  return (
    <div className="space-y-6" data-testid="user-management">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2" data-testid="users-title">Users</h1>
          <p className="text-slate-400">Manage system users and permissions</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="add-user-button">
              <Plus className="h-4 w-4 mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-white">Add New User</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="username" className="text-slate-300">Username *</Label>
                  <Input
                    id="username"
                    data-testid="user-username-input"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    required
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-300">Password *</Label>
                  <Input
                    id="password"
                    data-testid="user-password-input"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-slate-300">Full Name *</Label>
                  <Input
                    id="name"
                    data-testid="user-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-300">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role" className="text-slate-300">Role *</Label>
                  <Select
                    value={formData.role}
                    onValueChange={(value) => setFormData({ ...formData, role: value })}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700 text-white">
                      {roles.map((role) => (
                        <SelectItem key={role} value={role}>{role}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="department" className="text-slate-300">Department</Label>
                  <Input
                    id="department"
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="branch" className="text-slate-300">Branch (optional)</Label>
                  <Select
                    value={formData.branch_id || 'none'}
                    onValueChange={(v) => setFormData({ ...formData, branch_id: v === 'none' ? '' : v })}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="user-branch-select">
                      <SelectValue placeholder="No branch (HQ)" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700 text-white">
                      <SelectItem value="none">No branch (HQ)</SelectItem>
                      {branches.map((b) => (
                        <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 col-span-2">
                  <Label htmlFor="designation" className="text-slate-300">Designation</Label>
                  <Input
                    id="designation"
                    value={formData.designation}
                    onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                <div className="space-y-2 col-span-2 border-t border-slate-800 pt-3">
                  <Label className="text-slate-300">Employee Snap / Profile Photo</Label>
                  <div className="flex items-center space-x-3">
                    {formData.photo_url ? (
                      <img src={formData.photo_url} alt="preview" className="h-14 w-14 rounded-full object-cover border border-amber-500/60" data-testid="user-photo-preview" />
                    ) : (
                      <div className="h-14 w-14 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-500 text-xs">no photo</div>
                    )}
                    <Input type="file" accept="image/*" data-testid="user-photo-input"
                      onChange={(e) => {
                        const f = e.target.files?.[0]; if (!f) return;
                        if (f.size > 2 * 1024 * 1024) { toast.error('Photo must be under 2 MB'); return; }
                        const rd = new FileReader();
                        rd.onload = () => setFormData(fd => ({ ...fd, photo_url: rd.result }));
                        rd.readAsDataURL(f);
                      }}
                      className="bg-slate-800 border-slate-700 text-white file:text-amber-500 file:bg-transparent file:border-0" />
                    {formData.photo_url && (
                      <Button type="button" variant="ghost"
                        onClick={() => setFormData(fd => ({ ...fd, photo_url: '' }))}
                        className="text-slate-400 hover:text-white">Clear</Button>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="text-slate-300">
                  Cancel
                </Button>
                <Button type="submit" data-testid="submit-user-button" className="bg-amber-500 hover:bg-amber-600">
                  Create User
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <h2 className="text-xl font-semibold text-white">System Users</h2>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-400">Loading users...</div>
          ) : users.length === 0 ? (
            <div className="text-center py-8 text-slate-400">No users found</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800">
                  <TableHead className="text-slate-400">Username</TableHead>
                  <TableHead className="text-slate-400">Name</TableHead>
                  <TableHead className="text-slate-400">Email</TableHead>
                  <TableHead className="text-slate-400">Role</TableHead>
                  <TableHead className="text-slate-400">Employee ID</TableHead>
                  <TableHead className="text-slate-400">Department</TableHead>
                  <TableHead className="text-slate-400">Target</TableHead>
                  <TableHead className="text-slate-400">Badge</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                  <TableHead className="text-slate-400">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} className="border-slate-800 hover:bg-slate-800/50" data-testid="user-row">
                    <TableCell className="text-white font-medium">{user.username}</TableCell>
                    <TableCell className="text-white">{user.name}</TableCell>
                    <TableCell className="text-slate-300">{user.email || '-'}</TableCell>
                    <TableCell className="text-amber-500">{user.role}</TableCell>
                    <TableCell className="text-slate-300">{user.employee_id || '-'}</TableCell>
                    <TableCell className="text-slate-300">{user.department || '-'}</TableCell>
                    <TableCell className="text-slate-300" data-testid={`user-target-${user.id}`}>
                      {user.monthly_target ? user.monthly_target : <span className="text-slate-600">—</span>}
                    </TableCell>
                    <TableCell data-testid={`user-badge-cell-${user.id}`}>
                      {user.badge ? <UserBadge badge={user.badge} size="sm" testId={`user-badge-${user.id}`} /> : <span className="text-slate-600 text-xs">—</span>}
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-sm text-xs ${
                        user.is_active ? 'bg-emerald-500/20 text-emerald-500' : 'bg-red-500/20 text-red-500'
                      }`}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-1">
                        <Button size="sm" variant="ghost"
                          onClick={() => setEditDialog({ open: true, user })}
                          data-testid={`edit-user-${user.id}`}
                          className="text-blue-400 hover:text-blue-300 hover:bg-slate-800 h-8 w-8 p-0"
                          title="Edit user">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost"
                          onClick={() => setPermDialog({ open: true, user })}
                          data-testid={`edit-permissions-${user.id}`}
                          className="text-amber-500 hover:text-amber-400 hover:bg-slate-800 h-8 w-8 p-0"
                          title="Edit Permissions">
                          <Shield className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost"
                          disabled={certLoading === user.id}
                          onClick={async () => {
                            try {
                              setCertLoading(user.id);
                              const { data } = await axios.post(
                                `${API_URL}/api/hr/employment-verification/user/${user.id}`,
                                {}, { withCredentials: true }
                              );
                              downloadPdf(data.pdf, `EmploymentVerification_${user.username}.pdf`);
                              toast.success(`Certificate ${data.certificate_number} generated`);
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Failed to generate certificate');
                            } finally { setCertLoading(null); }
                          }}
                          data-testid={`gen-employment-cert-${user.id}`}
                          className="text-emerald-500 hover:text-emerald-400 hover:bg-slate-800 h-8 w-8 p-0"
                          title="Employment Verification Certificate">
                          <FileCheck className="h-4 w-4" />
                        </Button>
                        {currentUser?.id !== user.id && (
                          <Button size="sm" variant="ghost"
                            onClick={() => handleToggleStatus(user)}
                            data-testid={`toggle-status-${user.id}`}
                            className={`${user.is_active ? 'text-orange-400 hover:text-orange-300' : 'text-emerald-500 hover:text-emerald-400'} hover:bg-slate-800 h-8 w-8 p-0`}
                            title={user.is_active ? 'Deactivate user' : 'Activate user'}>
                            {user.is_active ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                          </Button>
                        )}
                        {currentUser?.role === 'Super Admin' && currentUser?.id !== user.id && (
                          <Button size="sm" variant="ghost"
                            onClick={() => handleDelete(user)}
                            data-testid={`delete-user-${user.id}`}
                            className="text-red-500 hover:text-red-400 hover:bg-slate-800 h-8 w-8 p-0"
                            title="Delete user">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <PermissionMatrixDialog
        open={permDialog.open}
        onOpenChange={(o) => setPermDialog({ ...permDialog, open: o })}
        user={permDialog.user}
      />
      <UserEditDialog
        open={editDialog.open}
        onOpenChange={(o) => setEditDialog({ ...editDialog, open: o })}
        user={editDialog.user}
        roles={roles}
        branches={branches}
        onSaved={fetchUsers}
      />
    </div>
  );
}